import {useSelector} from 'react-redux';
import {UserCategory, UserIdentifier, UserType} from 'src/types/enums';
import {Store} from 'src/types/store';

/**
 * Current Status:
 * Works for high school, transfer, ugss, adult, and parent
 */

const userIdentifierToUserType: Record<UserIdentifier, UserType> = {
  // Students
  [UserIdentifier.HS_STUDENT]: UserType.HS_STUDENT,
  [UserIdentifier.CC_STUDENT]: UserType.CC_STUDENT,
  [UserIdentifier.UG_STUDENT]: UserType.UNDERGRADUATE_STUDENT,
  [UserIdentifier.ADULT_LEARNER]: UserType.ADULT_LEARNER,

  // Other types
  [UserIdentifier.PARENT]: UserType.PARENT,
  [UserIdentifier.ADMIN]: UserType.PUBLIC,
  [UserIdentifier.CC_STAFF]: UserType.PUBLIC,
  [UserIdentifier.CP_USER]: UserType.COLLEGE_PORTAL_USER,
  [UserIdentifier.DISTRICT_ADMIN]: UserType.EDUCATOR,
  [UserIdentifier.PUBLIC]: UserType.PUBLIC,

  // Educators
  [UserIdentifier.MENTOR]: UserType.EDUCATOR,
  [UserIdentifier.TEACHER]: UserType.EDUCATOR,
  [UserIdentifier.COUNSELOR]: UserType.EDUCATOR,
  // TODO I wish these didn't exist
  [UserIdentifier.EDUCATOR]: UserType.EDUCATOR,
  [UserIdentifier.HS_COUNSELOR]: UserType.EDUCATOR,
  [UserIdentifier.HS_MENTOR]: UserType.EDUCATOR,
  [UserIdentifier.HS_TEACHER]: UserType.EDUCATOR,
};

// TODO: Implement a UserIdentifier to EducationPhase mapping

export const userIdentifierToCategory: Record<UserIdentifier, UserCategory> = {
  // Students
  [UserIdentifier.HS_STUDENT]: UserCategory.STUDENT,
  [UserIdentifier.CC_STUDENT]: UserCategory.STUDENT,
  [UserIdentifier.UG_STUDENT]: UserCategory.STUDENT,
  [UserIdentifier.ADULT_LEARNER]: UserCategory.STUDENT,

  // Other types
  [UserIdentifier.PARENT]: UserCategory.PARENT,
  [UserIdentifier.ADMIN]: UserCategory.ADMIN,
  [UserIdentifier.CC_STAFF]: UserCategory.PUBLIC,
  [UserIdentifier.CP_USER]: UserCategory.COLLEGE_PORTAL,
  [UserIdentifier.PUBLIC]: UserCategory.PUBLIC,

  // Educators
  [UserIdentifier.DISTRICT_ADMIN]: UserCategory.DISTRICT,
  [UserIdentifier.MENTOR]: UserCategory.EDUCATOR,
  [UserIdentifier.TEACHER]: UserCategory.EDUCATOR,
  [UserIdentifier.COUNSELOR]: UserCategory.EDUCATOR,
  // TODO I wish these UserIdentifiers didn't exist
  [UserIdentifier.EDUCATOR]: UserCategory.EDUCATOR,
  [UserIdentifier.HS_COUNSELOR]: UserCategory.EDUCATOR,
  [UserIdentifier.HS_MENTOR]: UserCategory.EDUCATOR,
  [UserIdentifier.HS_TEACHER]: UserCategory.EDUCATOR,
};

const useUserType = () => {
  const identifier: UserIdentifier = useSelector((state: Store) => state.userIdentifier);

  const isParent = identifier === UserIdentifier.PARENT;
  const isHighSchool = identifier === UserIdentifier.HS_STUDENT;
  const isTransfer = identifier === UserIdentifier.CC_STUDENT;
  const isAdult = identifier === UserIdentifier.ADULT_LEARNER;
  const isUndergraduate = identifier === UserIdentifier.UG_STUDENT;
  const isCollegePortalUser = identifier === UserIdentifier.CP_USER;
  const isMentor = identifier === UserIdentifier.MENTOR;
  const isCounselor = identifier === UserIdentifier.COUNSELOR;
  const isCCStaff = identifier === UserIdentifier.CC_STAFF;
  const isTeacher = identifier === UserIdentifier.TEACHER;
  const isEducator = isMentor || isCounselor || isCCStaff || isTeacher;
  const isDistrictAdmin = identifier === UserIdentifier.DISTRICT_ADMIN;

  // Higher level
  const isStudent = identifier?.toLowerCase().includes('student');

  // Level abstractions
  const userType: UserType = userIdentifierToUserType[identifier] || UserType.PUBLIC; // fallback to PUBLIC for unauthenticated users
  // const educationPhase =
  const userCategory: UserCategory = userIdentifierToCategory[identifier] || UserCategory.PUBLIC;

  return {
    isParent,
    isHighSchool,
    isTransfer,
    isStudent,
    isAdult,
    isUndergraduate,
    isCounselor,
    isMentor,
    isTeacher,
    isEducator,
    isCCStaff,
    isCollegePortalUser,
    isDistrictAdmin,
    userIdentifier: identifier,
    userType,
    userCategory,
  };
};

export default useUserType;
